import { useRouter } from "expo-router";
import { Languages } from "lucide-react-native";
import { FC, useMemo, useState } from "react";

import { Box, Center, HStack, Pressable, Text, VStack } from "@/components/ui";
import { useLocalization, useShareToken, useStudyContext } from "@/hooks";
import { Answer as AnswerType } from "@/types/resources";
import Answer from "./Answer";

/* ── Types ─────────────────────────────────────────────── */

interface VerbatimsPreviewProps {
  answers: AnswerType[];
  questionId: string;
  runId: string | null;
  selectedGroupIndex: "all" | string;
}

/* ── Component ──────────────────────────────────────────── */

const PREVIEW_COUNT = 4;
const PURPLE = "#7c3aed";

// Stable 32-bit FNV-1a hash. Used to pick a varied verbatims sample
// deterministically: calling Math.random() during render is impure and
// forbidden by the Rules of React (it breaks memoization and Strict Mode).
const hashString = (value: string): number => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
};

const VerbatimsPreview: FC<VerbatimsPreviewProps> = ({
  answers,
  questionId,
  runId,
  selectedGroupIndex,
}) => {
  const { i18n } = useLocalization();
  const router = useRouter();
  const shareToken = useShareToken();
  const isReadOnly = shareToken !== null;
  const { studyId } = useStudyContext();
  const [showOriginal, setShowOriginal] = useState(false);

  const withText = useMemo(
    () => answers.filter((a) => !!a.answer.text || !!a.answer.translated_text),
    [answers]
  );

  const total = withText.length;

  const preview = useMemo(() => {
    if (total <= PREVIEW_COUNT) return withText;
    // Deterministic pseudo-random sample: order by a stable hash of each
    // answer's text so the subset is varied yet pure across re-renders.
    return [...withText]
      .map((entry) => ({
        entry,
        rank: hashString(
          entry.answer.text ?? entry.answer.translated_text ?? ""
        ),
      }))
      .sort((a, b) => a.rank - b.rank)
      .slice(0, PREVIEW_COUNT)
      .map(({ entry }) => entry);
  }, [withText, total]);

  const hasAnyTranslation = useMemo(
    () => preview.some((a) => !!a.answer.translated_text),
    [preview]
  );

  return (
    <VStack className="gap-5">
      {/* Section header */}
      <HStack className="gap-2 flex-col items-start lg:flex-row lg:items-end lg:justify-between">
        <Text className="text-sm font-semibold text-typography-900 dark:text-typography-100">
          {i18n.t("dashboardNavigator.whatConsumersSaid")}
        </Text>

        <HStack className="w-full items-center justify-between gap-4 lg:w-auto">
          {hasAnyTranslation && (
            <Pressable
              onPress={() => setShowOriginal((v) => !v)}
              style={{
                alignItems: "center",
                backgroundColor: showOriginal ? "#f5f3ff" : "#fafafa",
                borderColor: showOriginal ? "#c4b5fd" : "#e5e7eb",
                borderRadius: 8,
                borderWidth: 1,
                flexDirection: "row",
                gap: 6,
                paddingHorizontal: 10,
                paddingVertical: 6,
              }}
            >
              <Languages
                color={showOriginal ? PURPLE : "hsl(220,10%,60%)"}
                size={13}
              />
              <Text
                className="text-xs font-medium"
                style={{ color: showOriginal ? PURPLE : "hsl(220,10%,46%)" }}
              >
                {showOriginal
                  ? i18n.t("misc.showTranslation")
                  : i18n.t("misc.showOriginal")}
              </Text>
            </Pressable>
          )}
          {total > PREVIEW_COUNT && runId && !isReadOnly && (
            <Pressable
              className="ml-auto"
              onPress={() =>
                studyId
                  ? router.push({
                      pathname: `/studies/${studyId}/personas`,
                      params: {
                        ...(selectedGroupIndex !== "all" && {
                          groupId: selectedGroupIndex,
                        }),
                        questionId,
                        runId,
                      },
                    })
                  : router.push({
                      pathname: `/adhoc/questions/${questionId}/personas`,
                      params: {
                        ...(selectedGroupIndex !== "all" && {
                          groupId: selectedGroupIndex,
                        }),
                        runId,
                      },
                    })
              }
            >
              <Text className="text-sm font-medium" style={{ color: PURPLE }}>
                {i18n.t("dashboardNavigator.seeAllCount", { count: total })} →
              </Text>
            </Pressable>
          )}
        </HStack>
      </HStack>

      {/* Grid — keep the section visible with a no-data state when empty */}
      {preview.length === 0 ? (
        <Center className="h-[120px]">
          <Text className="text-typography-500">
            {i18n.t("dashboardNavigator.noData")}
          </Text>
        </Center>
      ) : (
        <Box className="flex-row flex-wrap" style={{ gap: 16 }}>
          {preview.map((answer, idx) => (
            <Box
              key={idx}
              style={{
                flexBasis: "calc(25% - 12px)",
                minWidth: 220,
                flexGrow: 1,
              }}
            >
              <Answer
                answer={answer}
                questionId={questionId}
                runId={runId}
                showOriginal={showOriginal}
              />
            </Box>
          ))}
        </Box>
      )}
    </VStack>
  );
};

/* ── Export ─────────────────────────────────────────────── */

export default VerbatimsPreview;
